<template>
  <AdminLayout>
    <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="mb-6">
        <router-link to="/audit" class="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-3">
          <ArrowLeft class="w-4 h-4" /> Retour aux logs d'audit
        </router-link>
        <div class="flex items-center gap-3">
          <div class="p-2.5 bg-purple-50 rounded-xl">
            <Link2 class="w-6 h-6 text-purple-600" />
          </div>
          <div class="min-w-0">
            <h1 class="text-2xl sm:text-3xl font-bold text-dashboard-text">Corrélation par request_id</h1>
            <p class="text-xs text-dashboard-muted mt-1 font-mono break-all">{{ requestId }}</p>
          </div>
        </div>
      </div>

      <div v-if="loading" class="bg-white border border-dashboard-border rounded-xl p-16 text-center">
        <div class="w-10 h-10 border-4 border-[#22282F]/30 border-t-[#22282F] rounded-full animate-spin mx-auto mb-4" />
        <p class="text-dashboard-muted">Chargement…</p>
      </div>

      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle class="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <p class="font-medium text-red-800">Erreur</p>
          <p class="text-sm text-red-600 mt-1">{{ error }}</p>
        </div>
      </div>

      <div v-else>
        <!-- Résumé -->
        <div class="bg-white border border-dashboard-border rounded-xl shadow-sm p-4 mb-4">
          <div class="flex items-center gap-6 flex-wrap">
            <div>
              <p class="text-xs text-dashboard-muted">Entrées corrélées</p>
              <p class="text-2xl font-bold text-dashboard-text">{{ entries.length }}</p>
            </div>
            <div v-if="rootEntry?.user">
              <p class="text-xs text-dashboard-muted">Initié par</p>
              <router-link :to="`/audit/user/${rootEntry.user.id}`" class="text-sm font-medium text-blue-600 hover:underline">
                {{ rootEntry.user.full_name }}
              </router-link>
            </div>
            <div v-if="rootEntry?.ip_address">
              <p class="text-xs text-dashboard-muted">Depuis l'IP</p>
              <p class="text-sm font-mono text-dashboard-text">{{ rootEntry.ip_address }}</p>
            </div>
            <div v-if="rootEntry?.created_at">
              <p class="text-xs text-dashboard-muted">Démarrée</p>
              <p class="text-sm text-dashboard-text">{{ formatDateTime(rootEntry.created_at) }}</p>
            </div>
            <div v-if="firstSource">
              <p class="text-xs text-dashboard-muted">Source initiale</p>
              <p class="text-sm text-dashboard-text capitalize">{{ firstSource }}</p>
            </div>
          </div>
        </div>

        <!-- Liste timeline -->
        <div class="bg-white border border-dashboard-border rounded-xl shadow-sm overflow-hidden">
          <div class="px-4 py-3 border-b border-dashboard-border flex items-center gap-2">
            <History class="w-4 h-4 text-dashboard-muted" />
            <h2 class="text-sm font-semibold text-dashboard-text">Cascade d'événements</h2>
          </div>
          <div v-if="entries.length === 0" class="p-12 text-center text-sm text-dashboard-muted italic">
            Aucune entrée trouvée pour ce request_id.
          </div>
          <ul v-else class="divide-y divide-dashboard-border">
            <li v-for="entry in entries" :key="entry.id">
              <button
                @click="selectedLog = entry"
                :class="[
                  'w-full text-left px-4 py-3 hover:bg-dashboard-bg transition-colors flex items-start gap-3 border-l-4',
                  SEVERITY_META[entry.severity].ring,
                ]"
              >
                <component
                  :is="getDomainMeta(entry.action).icon"
                  class="w-4 h-4 mt-0.5 text-dashboard-muted flex-shrink-0"
                />
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span class="font-medium text-dashboard-text">{{ getActionLabel(entry.action) }}</span>
                    <span :class="['text-[10px] px-1.5 py-0.5 rounded font-medium', SOURCE_META[entry.source].badge]">
                      {{ entry.source }}
                    </span>
                    <span v-if="entry.severity !== 'info'" :class="['text-[10px] px-1.5 py-0.5 rounded font-medium', SEVERITY_META[entry.severity].badge]">
                      {{ SEVERITY_META[entry.severity].label }}
                    </span>
                  </div>
                  <div class="text-xs text-dashboard-muted mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>{{ formatDateTime(entry.created_at) }}</span>
                    <template v-if="entry.user">
                      <span>·</span>
                      <span>{{ entry.user.full_name }}</span>
                    </template>
                    <template v-if="entry.courrier_id">
                      <span>·</span>
                      <router-link :to="`/audit/courrier/${entry.courrier_id}`" @click.stop class="text-blue-600 hover:underline">
                        Courrier #{{ entry.courrier_id }}
                      </router-link>
                    </template>
                  </div>
                </div>
                <ChevronRight class="w-4 h-4 text-dashboard-muted flex-shrink-0 mt-1" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <AuditLogDetailPanel
      v-if="selectedLog"
      :log="selectedLog"
      @close="selectedLog = null"
    />
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  ArrowLeft, Link2, AlertCircle, History, ChevronRight,
} from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import AuditLogDetailPanel from '@/components/audit/AuditLogDetailPanel.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'
import {
  SEVERITY_META, SOURCE_META, getActionLabel, getDomainMeta, formatDateTime,
} from '@/utils/auditMeta'
import type { AuditLogEntry } from '@/utils/auditMeta'

const route = useRoute()
const toast = useToast()

const requestId = computed(() => String(route.params.uuid))
const entries = ref<AuditLogEntry[]>([])
const loading = ref(false)
const error = ref('')
const selectedLog = ref<AuditLogEntry | null>(null)

const rootEntry = computed<AuditLogEntry | null>(() => entries.value[0] || null)
const firstSource = computed(() => entries.value.find(e => e.source === 'frontend')?.source || rootEntry.value?.source || null)

async function fetchEntries() {
  loading.value = true
  error.value = ''
  try {
    const result = await apiFetch(`/api/admin/audit/request/${requestId.value}/`)
    entries.value = result.entries || []
  } catch (e: any) {
    error.value = e?.message || 'Erreur'
    toast.error('Erreur', error.value)
  } finally {
    loading.value = false
  }
}

watch(requestId, fetchEntries)
onMounted(fetchEntries)
</script>
